import React from 'react';

export type NavigationTab =
  | 'dashboard'
  | 'tasks'
  | 'partners'
  | 'connections'
  | 'transfer-history';

export interface NavigationProps {
  activeTab: NavigationTab;
  onTabChange: (tab: NavigationTab) => void;
  className?: string;
}

interface TabConfig {
  id: NavigationTab;
  label: string;
  icon?: string;
  description?: string;
  primary?: boolean;
}

const tabConfigs: TabConfig[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: '\uD83D\uDCCA',
    description: 'System overview and statistics',
    primary: true
  },
  {
    id: 'partners',
    label: 'Partners',
    icon: '\uD83E\uDD1D',
    description: 'Partner management and monitoring',
    primary: true
  },
  {
    id: 'connections',
    label: 'Connections',
    icon: '\uD83D\uDD17',
    description: 'Connection management and testing',
    primary: true
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: '\u2699\uFE0F',
    description: 'Task management and configuration',
    primary: true
  },
  {
    id: 'transfer-history',
    label: 'Transfer History',
    icon: '\uD83D\uDCCB',
    description: 'Search and track file journeys',
    primary: true
  }
];

export const Navigation: React.FC<NavigationProps> = ({
  activeTab,
  onTabChange,
  className = ''
}) => {
  const handleTabClick = (tabId: NavigationTab) => {
    onTabChange(tabId);
  };

  const handleKeyDown = (event: React.KeyboardEvent, tabId: NavigationTab) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onTabChange(tabId);
    }
  };

  const renderTab = (tab: TabConfig) => {
    const isActive = activeTab === tab.id;
    return (
      <button
        key={tab.id}
        className={`
          relative flex items-center gap-2
          px-4 py-3 lg:px-5
          text-sm lg:text-base font-medium
          whitespace-nowrap
          rounded-lg
          transition-all duration-200
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
          ${isActive
            ? 'bg-primary-600 text-white dark:bg-primary-500'
            : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
          }
        `}
        onClick={() => handleTabClick(tab.id)}
        onKeyDown={(e) => handleKeyDown(e, tab.id)}
        title={tab.description}
        aria-label={`Navigate to ${tab.label}`}
        aria-current={isActive ? 'page' : undefined}
      >
        {tab.icon && (
          <span className="text-lg leading-none hidden sm:inline">{tab.icon}</span>
        )}
        <span className="leading-none">{tab.label}</span>
      </button>
    );
  };

  return (
    <nav
      className={`
        bg-white dark:bg-neutral-900
        border-b border-neutral-200 dark:border-neutral-800
        shadow-sm
        ${className}
      `}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-center min-h-[60px]">
          <div className="flex items-center gap-1 lg:gap-2 flex-wrap justify-center">
            {tabConfigs.map(tab => renderTab(tab))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;